import React from 'react';

import { Col } from '@opendoor/atoms/box';
import { corePdpLink } from 'components/helpers/links';
import type { HubBuyingFavorite } from 'components/hub/useHubBuyerListingsAndTours';
import {
  buildPropertyPhotoUrl,
  buildPropertySpecsLine,
} from 'components/marketplace/property/helpers';
import { formatCurrency } from 'helpers/formatters';

import { HubCarousel } from './HubCarousel';
import { HubSectionHeader } from './HubSectionHeader';
import { isSavedHomesSectionVisible } from './hubSectionVisibility';
import { SavedHomesSkeleton } from './HubSkeletons';
import { SavedHomeCard } from './SavedHomeCard';
import { useHubCarousel } from './useHubCarousel';

interface SavedHomesSectionProps {
  properties: HubBuyingFavorite[];
  isLoading: boolean;
}

export const SavedHomesSection: React.FC<SavedHomesSectionProps> = ({ properties, isLoading }) => {
  const { scrollRef, scrollPrev, scrollNext, canScroll } = useHubCarousel(properties.length);

  if (!isSavedHomesSectionVisible(properties, isLoading)) {
    return null;
  }

  const hasProperties = !isLoading && properties.length > 0;

  return (
    <Col className="w-full gap-5">
      <HubSectionHeader
        title="Saved homes"
        showControls={hasProperties && canScroll}
        onPrev={scrollPrev}
        onNext={scrollNext}
      />
      {isLoading ? (
        <SavedHomesSkeleton />
      ) : (
        <HubCarousel ref={scrollRef} bleedOnMobile>
          {properties.map((property) => {
            const href = corePdpLink(property.slug ?? '_', property.addressId);
            const addressLine = property.street?.trim() || 'Saved home';
            return (
              <SavedHomeCard
                key={property.addressId}
                section="saved-homes"
                href={href}
                photoUrl={buildPropertyPhotoUrl(property.photoUrls[0], {
                  preset: '3:2-md',
                  presetScale: '2/3',
                })}
                addressLine={addressLine}
                priceLabel={
                  property.listPriceCents != null ? formatCurrency(property.listPriceCents) : null
                }
                detailsLine={buildPropertySpecsLine(
                  property.bedrooms,
                  property.bathrooms,
                  property.totalLivingSquareFootage,
                )}
              />
            );
          })}
        </HubCarousel>
      )}
    </Col>
  );
};
